import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import PermisosIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SecurityIcon from '@mui/icons-material/Security';
import KeyIcon from '@mui/icons-material/Key';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';

import CollapsibleTable from '../../components/table';
import BotonAgregar from '../../components/BotonAgregar';
import ExportButton from '../../components/BotonEx';
import ModalPermisos from '../../components/ModalPermisos';
import ModalUsuarios from '../../components/ModalUsuarios';
import DoubleConfirmModal from '../../components/DoubleConfirmModal';
import { Box, Paper, TextField, Autocomplete, Button, Chip, Stack, Typography, Switch, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { useSession } from '../../components/hooks/useSession';
import config from '../../config/config';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import { getUsuarioId, getEmpresaIdForPrefs } from '../../utils/userPrefs';

// Helpers sesión
const getSessionString = (key) => sessionStorage.getItem(key) || '';

const getSessionArray = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('Error parseando', key, e);
    return [];
  }
};

// Normaliza idEmpresa
const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];

  if (Array.isArray(idEmpresa)) {
    return idEmpresa.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }

  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();

    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) {
          return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
        }
      } catch {}
    }

    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }

  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

// Helpers para filtros multiselect
const splitFilterValue = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return String(val).split(',').map(s => s.trim()).filter(Boolean);
};

export default function Usuarios() {
  const apiUrl = config.apiBaseUrl;

  const sessionData = useSession();
  const { idPerfil, nombrePerfil } = sessionData;

  const [loggedInUsername, setLoggedInUsername] = useState(getSessionString('loggedInUsername'));
  const [idEmpresa, setIdEmpresa] = useState(getSessionArray('EmpresaId'));
  const [nombreEmpresa, setNombreEmpresa] = useState(getSessionString('EmpresaNombre'));

  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);

  const empresaIdsKey = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length ? arr.join(',') : '';
  }, [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaIdsKey) return [];
    return empresaIdsKey
      .split(',')
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaIdsKey]);

  const empresaIdPref = useMemo(() => {
    return empresaIds.length > 1 ? 0 : (empresaIds[0] ?? 0);
  }, [empresaIds]);

  useEffect(() => {
    const handleEmpresaChange = () => {
      setLoggedInUsername(getSessionString('loggedInUsername'));
      setIdEmpresa(getSessionArray('EmpresaId'));
      setNombreEmpresa(getSessionString('EmpresaNombre'));
    };
    window.addEventListener('empresaCambiada', handleEmpresaChange);
    return () => window.removeEventListener('empresaCambiada', handleEmpresaChange);
  }, []);

  const [data, setData] = useState({ contenido: [] });
  const [dataEmpresa, setEmpresa] = useState([]);
  const [dataRool, setdataRool] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editPasswordOnly, setEditPasswordOnly] = useState(false);

  const [formData, setFormData] = useState({
    USUARIOID: null,
    TIPOUSERID: null,
    USERNAME: '',
    PASSWORD: '',
    empresas: [],
    TIPOUSER: null,
    CORREO: '',
    SESSION: loggedInUsername,
    accesos: {},
    requiere_2fa: 0,
    permitir_password: 1,
    permitir_microsoft: 1,
  });

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: '',
    action: '',
    row: null,
  });

  const initialPageSize = useMemo(() => {
    try {
      const uId = getUsuarioId();
      const eId = getEmpresaIdForPrefs();
      const key = `table_prefs__Usuarios_v2__layout${uId ? `__u${uId}` : ''}${eId ? `__e${eId}` : ''}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.rowsPerPage && Number.isFinite(Number(parsed.rowsPerPage))) {
          return Number(parsed.rowsPerPage);
        }
      }
    } catch (e) {}
    return 10;
  }, []);

  const [paginationInfo, setPaginationInfo] = useState({ page: 1, pageSize: initialPageSize });
  const [totalServerRows, setTotalServerRows] = useState(0);
  const [loadingData, setLoadingData] = useState(false);

  const fetchData = useCallback(() => {
    if (!apiUrl) return;
    if (!empresaIdsKey) return;

    setLoadingData(true);

    axios
      .post(`${apiUrl}/usuariosTabla`, {
        nombrePerfil,
        nombreEmpresa,
        idEmpresa: empresaIds,
        idPerfil,
        filterData: {
          page: 1,
          pageSize: 1000,
        },
      })
      .then((res) => {
        if (res.data?.mensaje) {
          handleOpenSweetAlert(res.data.mensaje, 'error');
          setData({ contenido: [] });
          setTotalServerRows(0);
        } else {
          const contenido = Array.isArray(res.data?.contenido) ? res.data.contenido : (Array.isArray(res.data) ? res.data : []);
          setData({ contenido });
          setTotalServerRows(res.data?.totalRecords || contenido.length);
        }
      })
      .catch((err) => {
        const mensajeError = err.response?.data?.error || err.response?.data?.mensaje || 'Error al procesar la solicitud';
        handleOpenSweetAlert(mensajeError, 'error');
        setData({ contenido: [] });
        setTotalServerRows(0);
      })
      .finally(() => {
        setLoadingData(false);
      });
  }, [apiUrl, nombrePerfil, nombreEmpresa, empresaIds, empresaIdsKey, idPerfil]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (!empresaIdsKey) return;

        const empresaRes = await axios.post(`${apiUrl}/empresasDisponibles`, {
          idEmpresa: empresaIds,
        });
        setEmpresa(empresaRes.data || []);

        const roolRes = await axios.post(`${apiUrl}/usuariosTipoUsuario`);
        setdataRool(roolRes.data || []);
      } catch (err) {
        console.error('Error al cargar datos iniciales:', err);
      }
    };

    fetchInitialData();
  }, [apiUrl, empresaIds, empresaIdsKey]);

  // Heartbeat para estatus en línea
  useEffect(() => {
    const sendHeartbeat = () => {
      const uId = sessionStorage.getItem('UsuarioId');
      const uName = sessionStorage.getItem('username') || sessionStorage.getItem('loggedInUsername');
      if (apiUrl && (uId || uName)) {
        axios.post(`${apiUrl}/api/usuario/heartbeat`, { usuarioId: uId, username: uName }).catch(() => {});
      }
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(interval);
  }, [apiUrl]);

  const createUpdate = async () => {
    try {
      const nuevo = !formData.USUARIOID;
      const cambiandoPass = formData.USUARIOID && formData.PASSWORD;

      const mensajeExito = nuevo
        ? 'Usuario creado con éxito'
        : cambiandoPass
          ? 'Contraseña actualizada con éxito'
          : 'Datos del usuario actualizados con éxito';

      await axios.post(`${apiUrl}/usuariosCrearEditar`, {
        formData: {
          ...formData,
          SESSION: loggedInUsername
        }
      });

      handleCloseModal();
      handleOpenSweetAlert(mensajeExito, 'success');
      fetchData();
    } catch (err) {
      const errMsg = err.response?.data?.Mensaje || err.response?.data?.message || err.response?.data?.error || 'Error al guardar los datos';
      handleOpenSweetAlert(errMsg, 'error');
      console.error('Error en la creación o actualización del usuario:', err);
    }
  };

  const eliminarUsuario = (row) => {
    axios
      .post(`${apiUrl}/usuariosEliminar/`, {
        valorBool: 0,
        USUARIOID: row.USUARIOID,
        SESSION: loggedInUsername,
      })
      .then(() => {
        handleOpenSweetAlert('Registro eliminado con éxito', 'success');
        fetchData();
      })
      .catch((err) => {
        console.error('Error eliminando usuario:', err);
        handleOpenSweetAlert('Error al eliminar el usuario', 'error');
      });
  };

  const cambiarEstadoUsuario = useCallback(
    async (row) => {
      const nuevoEstado = row.ACTIVO ? 0 : 1;

      try {
        await axios.post(`${apiUrl}/usuariosEliminar/`, {
          valorBool: nuevoEstado,
          USUARIOID: row.USUARIOID,
          SESSION: loggedInUsername,
        });

        handleOpenSweetAlert(
          nuevoEstado ? 'Usuario activado con éxito' : 'Usuario desactivado con éxito',
          'success'
        );
        fetchData();
      } catch (err) {
        console.error('Error cambiando estado del usuario:', err);
        handleOpenSweetAlert('Error al cambiar el estado del usuario', 'error');
      }
    },
    [apiUrl, loggedInUsername, fetchData]
  );

  // ─── Desbloquear Usuario ──────────────────────────────────────────────
  const handleDesbloquearUsuario = useCallback(async (row) => {
    try {
      const res = await axios.post(`${apiUrl}/api/usuarios/desbloquear`, {
        usuarioID: row.USUARIOID,
        SESSION: loggedInUsername,
      });
      handleOpenSweetAlert(res.data?.message || 'Usuario desbloqueado con éxito', 'success');
      fetchData();
    } catch (err) {
      console.error('Error desbloqueando usuario:', err);
      handleOpenSweetAlert(err.response?.data?.message || 'Error al desbloquear el usuario', 'error');
    }
  }, [apiUrl, loggedInUsername, fetchData]);

  // ─── Configuración Global del Sistema (Switch Login Microsoft) ──────
  const [mostrarMicrosoftGlobal, setMostrarMicrosoftGlobal] = useState(false);

  const fetchGlobalConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/sistema/configuracion`);
      const configs = res.data?.configuraciones || [];
      const msConfig = configs.find(c => c.CLAVE === 'LOGIN_MOSTRAR_MICROSOFT');
      if (msConfig) {
        setMostrarMicrosoftGlobal(msConfig.VALOR === '1');
      }
    } catch (e) {
      console.error('Error cargando configuración global:', e);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchGlobalConfig();
  }, [fetchGlobalConfig]);

  const handleToggleMicrosoftGlobal = async (checked) => {
    const nuevoValor = checked ? '1' : '0';
    setMostrarMicrosoftGlobal(checked);
    try {
      await axios.post(`${apiUrl}/api/sistema/configuracion`, {
        clave: 'LOGIN_MOSTRAR_MICROSOFT',
        valor: nuevoValor,
        sessionUser: loggedInUsername,
      });
      handleOpenSweetAlert(
        checked
          ? 'Botón de Microsoft SSO activado y visible en la pantalla de Login.'
          : 'Botón de Microsoft SSO desactivado y oculto en la pantalla de Login.',
        'success'
      );
    } catch (err) {
      console.error('Error actualizando configuración global:', err);
      setMostrarMicrosoftGlobal(!checked);
      handleOpenSweetAlert('Error al actualizar la configuración global.', 'error');
    }
  };

  // ─── Toggle Métodos de Acceso (Microsoft, Password, 2FA) ─────────────
  const handleToggleMetodo = useCallback(async (row, metodo) => {
    const nombreUsuario = row.USER_NAME || row.USERNAME || `Usuario ${row.USUARIOID}`;
    let valorActual = 0;
    if (metodo === 'microsoft') valorActual = Number(row.permitir_microsoft) === 1 ? 1 : 0;
    if (metodo === 'password') valorActual = Number(row.permitir_password) === 1 ? 1 : 0;
    if (metodo === '2fa') valorActual = Number(row.requiere_2fa) === 1 ? 1 : 0;

    const nuevoValor = valorActual === 1 ? 0 : 1;

    try {
      const res = await axios.post(`${apiUrl}/api/usuarios/toggle-metodo-acceso`, {
        usuarioID: row.USUARIOID,
        metodo,
        valor: nuevoValor,
        SESSION: loggedInUsername,
      });

      handleOpenSweetAlert(res.data?.message || 'Acceso actualizado con éxito.', 'success');
      fetchData();
    } catch (err) {
      console.error('Error modificando método de acceso:', err);
      handleOpenSweetAlert(err.response?.data?.message || 'Error al actualizar método de acceso', 'error');
    }
  }, [apiUrl, loggedInUsername, fetchData]);

  // ─── Acciones y utilidades ────────────────────────────────────────────
  const handleEditarUsuario = (row) => {
    const { USER_NAME, EMPRESAID, TIPOUSERID, CORREO, USUARIOID, ACCESOS_JSON, COMPRADOR_ERP_VARIANTS, requiere_2fa, permitir_password, permitir_microsoft } = row;

    setFormData({
      USUARIOID: USUARIOID || '',
      USERNAME: USER_NAME || '',
      PASSWORD: '',
      COMPRADOR_ERP_VARIANTS: COMPRADOR_ERP_VARIANTS || '',
      requiere_2fa: Number(requiere_2fa) === 1 ? 1 : 0,
      permitir_password: permitir_password !== undefined ? (Number(permitir_password) === 1 ? 1 : 0) : 1,
      permitir_microsoft: permitir_microsoft !== undefined ? (Number(permitir_microsoft) === 1 ? 1 : 0) : 1,
      empresas: EMPRESAID
        ? Array.from(
            new Set(
              String(EMPRESAID)
                .split(',')
                .map((id) => parseInt(id.trim(), 10))
                .filter((n) => !isNaN(n))
            )
          )
        : [],
      TIPOUSER: TIPOUSERID || '',
      TIPOUSERID: TIPOUSERID || null,
      CORREO: CORREO || '',
      SESSION: loggedInUsername,
      accesos: (() => {
        try {
          if (ACCESOS_JSON) {
            const parsed = JSON.parse(ACCESOS_JSON);
            return Object.fromEntries(
              Object.entries(parsed || {}).map(([k, v]) => [
                String(k),
                {
                  centros: Array.isArray(v?.centros) ? v.centros.map(Number) : [],
                  departamentos: Array.isArray(v?.departamentos)
                    ? v.departamentos.map(Number)
                    : [],
                },
              ])
            );
          }
        } catch (e) {
          console.error('Error parseando ACCESOS_JSON:', e);
        }
        return {};
      })(),
    });

    setModalOpen(true);
    setEditPasswordOnly(false);
  };

  const handlePermisosUsuario = (user) => {
    setSelectedUser(user);
    setPermissionModalOpen(true);
  };

  const cerrarPermisosUsuario = () => {
    setPermissionModalOpen(false);
  };

  const handlePermisosGuardados = () => {
    setPermissionModalOpen(false);
    fetchData();
  };

  const confirmEliminarUsuario = (row) => {
    setConfirmModal({
      open: true,
      action: 'eliminar',
      row,
      message: '¿Está seguro de que desea eliminar el usuario?',
    });
  };

  const handleConfirmAction = () => {
    if (confirmModal.action === 'eliminar') {
      eliminarUsuario(confirmModal.row);
    } else if (confirmModal.action === 'cambiarEstado') {
      cambiarEstadoUsuario(confirmModal.row);
    }

    setConfirmModal({ open: false, action: '', row: null, message: '' });
  };

  const handleCancelAction = () => {
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setFormData({
      USUARIOID: null,
      TIPOUSERID: null,
      USERNAME: '',
      PASSWORD: '',
      empresas: [],
      TIPOUSER: null,
      CORREO: '',
      SESSION: loggedInUsername,
      accesos: {},
      requiere_2fa: 0,
      permitir_password: 1,
      permitir_microsoft: 1,
    });
    setEditPasswordOnly(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ─── Botones ──────────────────────────────────────────────────────────
  const buttons = useMemo(() => [
    {
      id: 1,
      label: 'Editar Usuarios',
      onClick: handleEditarUsuario,
      icon: <EditIcon />,
      buttonColor: '#2196f3',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !Number(row.ACTIVO),
    },
    {
      id: 2,
      label: 'Asignar Permisos',
      onClick: handlePermisosUsuario,
      icon: <PermisosIcon />,
      buttonColor: '#455a64',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !Number(row.ACTIVO),
    },
    {
      id: 3,
      label: (row) => (row.ACTIVO ? 'Desactivar' : 'Activar'),
      onClick: (row) => {
        const mensaje = row.ACTIVO
          ? '¿Está seguro de desactivar este usuario?'
          : '¿Está seguro de activar este usuario?';

        setConfirmModal({
          open: true,
          action: 'cambiarEstado',
          row,
          message: mensaje,
        });
      },
      icon: (row) => (row.ACTIVO ? <BlockIcon /> : <CheckCircleIcon />),
      buttonColor: (row) => (row.ACTIVO ? '#e53935' : '#43a047'),
      buttonFontColor: '#ffffff',
      isVisible: true,
    },
    {
      id: 4,
      label: 'Desbloquear Acceso',
      onClick: handleDesbloquearUsuario,
      icon: <LockOpenIcon />,
      buttonColor: '#f59e0b',
      buttonFontColor: '#ffffff',
      isVisible: (row) => Number(row.esta_bloqueado) === 1 || (row.bloqueado_hasta && new Date(row.bloqueado_hasta) > new Date()),
    },
    {
      id: 5,
      label: (row) => Number(row?.permitir_microsoft) === 1 ? '☁️ Bloquear Microsoft' : '☁️ Habilitar Microsoft',
      onClick: (row) => handleToggleMetodo(row, 'microsoft'),
      icon: <CloudQueueIcon />,
      buttonColor: (row) => Number(row?.permitir_microsoft) === 1 ? '#0078d4' : '#64748b',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !Number(row.ACTIVO),
    },
    {
      id: 6,
      label: (row) => Number(row?.permitir_password) === 1 ? '🔑 Bloquear Contraseña' : '🔑 Habilitar Contraseña',
      onClick: (row) => handleToggleMetodo(row, 'password'),
      icon: <KeyIcon />,
      buttonColor: (row) => Number(row?.permitir_password) === 1 ? '#2563eb' : '#64748b',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !Number(row.ACTIVO),
    },
    {
      id: 7,
      label: (row) => Number(row?.requiere_2fa) === 1 ? '🔒 Desactivar 2FA (Directo)' : '🛡️ Activar 2FA (Exigir Código)',
      onClick: (row) => handleToggleMetodo(row, '2fa'),
      icon: <SecurityIcon />,
      buttonColor: (row) => Number(row?.requiere_2fa) === 1 ? '#16a34a' : '#64748b',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !Number(row.ACTIVO) || Number(row.permitir_password) === 0,
    },
  ], [cambiarEstadoUsuario, handleDesbloquearUsuario, handleToggleMetodo]);

  // ─── Estados de filtros en encabezado (Estilo Solicitudes) ─────────────
  const [empresaFilter, setEmpresaFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [quickSearch, setQuickSearch] = useState('');

  // ─── Opciones de Filtro Dinámicas ────────────────────────────────────
  const empresaOptions = useMemo(() => {
    const set = new Set();
    (data.contenido || []).forEach(row => {
      const emp = row.NOMBRE_EMPRESA || row.EMPRESA || '';
      if (emp) {
        emp.split(',').forEach(e => {
          const clean = e.trim();
          if (clean) set.add(clean);
        });
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [data.contenido]);

  const tipoUserOptions = useMemo(() => {
    const set = new Set();
    (data.contenido || []).forEach(row => {
      const tipo = row.TIPO_USER || row.TIPO_USUARIO || '';
      if (tipo && tipo.trim()) set.add(tipo.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [data.contenido]);

  // ─── Contenido Filtrado Mapeado ───────────────────────────────────────
  const dataForTable = useMemo(() => {
    let rows = Array.isArray(data?.contenido) ? data.contenido : [];

    const selectedEmpresas = splitFilterValue(empresaFilter);
    if (selectedEmpresas.length > 0) {
      rows = rows.filter(row => {
        const empString = row.NOMBRE_EMPRESA || row.EMPRESA || '';
        return selectedEmpresas.some(selected => empString.toLowerCase().includes(selected.toLowerCase()));
      });
    }

    const selectedTipos = splitFilterValue(tipoFilter);
    if (selectedTipos.length > 0) {
      rows = rows.filter(row => {
        const tipoString = row.TIPO_USER || row.TIPO_USUARIO || '';
        return selectedTipos.some(selected => tipoString.toLowerCase().includes(selected.toLowerCase()));
      });
    }

    if (quickSearch && quickSearch.trim()) {
      const q = quickSearch.toLowerCase().trim();
      rows = rows.filter(row => (
        String(row.USUARIOID || '').toLowerCase().includes(q) ||
        String(row.USER_NAME || '').toLowerCase().includes(q) ||
        String(row.CORREO || '').toLowerCase().includes(q) ||
        String(row.TIPO_USER || row.TIPO_USUARIO || '').toLowerCase().includes(q) ||
        String(row.NOMBRE_EMPRESA || row.EMPRESA || '').toLowerCase().includes(q)
      ));
    }

    const mappedRows = rows.map(row => {
      const isActivo = Boolean(Number(row.ACTIVO));
      const empresasList = (row.NOMBRE_EMPRESA || row.EMPRESA || '').split(',').map(e => e.trim()).filter(Boolean);

      const actDate = row.ultima_actividad ? new Date(row.ultima_actividad) : null;
      let estatusConexion = row.estatus_conexion || '⚪ Desconectado';
      let tiempoRelativo = row.tiempo_relativo || 'Sin registro';

      if (actDate && !isNaN(actDate.getTime())) {
        const diffMs = Date.now() - actDate.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);

        if (diffMins < 2) {
          estatusConexion = '🟢 En Línea';
          tiempoRelativo = 'Activo ahora';
        } else if (diffMins < 10) {
          estatusConexion = '🟡 Ausente';
          tiempoRelativo = `Hace ${diffMins} min`;
        } else if (diffMins < 60) {
          estatusConexion = '⚪ Desconectado';
          tiempoRelativo = `Hace ${diffMins} min`;
        } else if (diffHours < 24) {
          estatusConexion = '⚪ Desconectado';
          tiempoRelativo = `Hace ${diffHours} h`;
        } else {
          estatusConexion = '⚪ Desconectado';
          const diffDays = Math.floor(diffHours / 24);
          tiempoRelativo = `Hace ${diffDays} días`;
        }
      }

      const isBloqueado = Number(row.esta_bloqueado) === 1 || (row.bloqueado_hasta && new Date(row.bloqueado_hasta) > new Date());
      const estatusSeguridad = isBloqueado
        ? `🚫 Bloqueado (${row.intentos_fallidos || 5} intentos)`
        : (isActivo ? 'Activo' : 'Inactivo');

      const compactRow = {
        USUARIO_USUARIO_ID: row.USER_NAME || row.USERNAME || 'N/D',
        ESTATUS_CONEXION: estatusConexion,
        SEGURIDAD_2FA: Number(row.requiere_2fa) === 1 ? '🔒 2FA Activo' : '🔓 Directo',
        TIEMPO_ACTIVIDAD: tiempoRelativo,
        ULTIMA_IP: row.ultima_ip || 'Sin registro',
        DISPOSITIVO: row.ultimo_dispositivo || 'Navegador Web',
        CORREO: row.CORREO || 'N/D',
        TIPO_USUARIO: row.TIPO_USER || row.TIPO_USUARIO || 'N/D',
        EMPRESAS_ASIGNADAS: row.NOMBRE_EMPRESA || row.EMPRESA || '',
        ESTATUS_COMPACTO: estatusSeguridad,
        ACTIVO: row.ACTIVO,
        requiere_2fa: row.requiere_2fa != null ? Number(row.requiere_2fa) : 0,
        permitir_password: row.permitir_password != null ? Number(row.permitir_password) : 1,
        permitir_microsoft: row.permitir_microsoft != null ? Number(row.permitir_microsoft) : 1,
        USUARIOID: row.USUARIOID,
        USER_NAME: row.USER_NAME || row.USERNAME,
        esta_bloqueado: row.esta_bloqueado,
        bloqueado_hasta: row.bloqueado_hasta,
      };

      Object.keys(row || {}).forEach((key) => {
        if (key === 'partidas') return;
        Object.defineProperty(compactRow, key, {
          value: row[key],
          enumerable: false,
          configurable: true,
          writable: true,
        });
      });

      return compactRow;
    });

    mappedRows.sort((a, b) => {
      const aActivo = Number(a.ACTIVO) === 1 ? 1 : 0;
      const bActivo = Number(b.ACTIVO) === 1 ? 1 : 0;
      return bActivo - aActivo;
    });

    return mappedRows;
  }, [data, empresaFilter, tipoFilter, quickSearch, totalServerRows]);

  const handleResetFilters = () => {
    setEmpresaFilter('');
    setTipoFilter('');
    setQuickSearch('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', p: 'clamp(6px, 0.8vw, 12px)', gap: 'clamp(6px, 0.8vw, 10px)', bgcolor: '#f8fafc' }}>
      {/* Barra de Filtros */}
      <Paper
        elevation={0}
        sx={{
          flexShrink: 0,
          px: 'clamp(8px, 1vw, 12px)',
          py: 'clamp(5px, 0.6vw, 8px)',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(4px, 0.5vw, 8px)',
          flexWrap: 'wrap',
          '& .MuiOutlinedInput-root': {
            height: 'clamp(26px, 2.2vw, 32px) !important',
            fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem) !important',
            borderRadius: '7px !important',
          },
          '& .MuiInputLabel-root': {
            fontSize: 'clamp(0.62rem, 0.58rem + 0.05vw, 0.70rem) !important',
            top: '-3px !important',
          }
        }}
      >
        <Autocomplete
          multiple
          forcePopupIcon
          filterSelectedOptions
          size="small"
          options={empresaOptions}
          value={splitFilterValue(empresaFilter)}
          onChange={(_, value) => setEmpresaFilter(value.join(','))}
          renderInput={(params) => <TextField {...params} label="Empresa" placeholder="Empresas..." sx={{ width: 'clamp(150px, 16vw, 220px)' }} />}
          sx={{ width: 'clamp(150px, 16vw, 220px)' }}
        />

        <Autocomplete
          multiple
          forcePopupIcon
          filterSelectedOptions
          size="small"
          options={tipoUserOptions}
          value={splitFilterValue(tipoFilter)}
          onChange={(_, value) => setTipoFilter(value.join(','))}
          renderInput={(params) => <TextField {...params} label="Tipo Usuario" placeholder="Roles..." sx={{ width: 'clamp(140px, 14vw, 195px)' }} />}
          sx={{ width: 'clamp(140px, 14vw, 195px)' }}
        />

        <TextField
          size="small"
          label="Búsqueda rápida"
          value={quickSearch}
          onChange={(e) => setQuickSearch(e.target.value)}
          placeholder="Buscar..."
          sx={{ minWidth: 'clamp(140px, 16vw, 220px)', flexGrow: 1 }}
        />

        <Box sx={{ display: 'flex', gap: 0.6, ml: 'auto' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<SearchIcon sx={{ fontSize: 'clamp(13px, 1vw, 16px) !important' }} />}
            sx={{
              height: 'clamp(26px, 2.2vw, 32px)',
              minWidth: 0,
              px: 'clamp(8px, 0.8vw, 14px)',
              fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem)',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '7px',
              background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
              boxShadow: '0 2px 6px rgba(6, 85, 165, 0.2)',
              '&:hover': {
                background: 'linear-gradient(135deg, #054785 0%, #0655A5 100%)',
                boxShadow: '0 3px 8px rgba(6, 85, 165, 0.3)'
              }
            }}
          >
            Buscar
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleResetFilters}
            startIcon={<RestartAltIcon sx={{ fontSize: 'clamp(13px, 1vw, 16px) !important' }} />}
            sx={{
              height: 'clamp(26px, 2.2vw, 32px)',
              minWidth: 0,
              px: 'clamp(8px, 0.8vw, 12px)',
              fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem)',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '7px',
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
            }}
          >
            Limpiar
          </Button>
        </Box>
      </Paper>

      {/* Contenedor de Tabla */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <CollapsibleTable
          dataTable={dataForTable}
          buttons={buttons}
          leftButtons={
            <Stack direction="row" spacing={1} alignItems="center">
              <BotonAgregar
                title="Agregar Usuario"
                onClick={() => {
                  setModalOpen(true);
                  setEditPasswordOnly(false);
                }}
              />
              <Tooltip title="Activa o desactiva la visibilidad del botón 'Iniciar sesión con Microsoft' en la pantalla principal de Login.">
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.8,
                    px: 1.2,
                    py: 0.3,
                    borderRadius: '8px',
                    bgcolor: mostrarMicrosoftGlobal ? '#eff6ff' : '#f8fafc',
                    border: `1.5px solid ${mostrarMicrosoftGlobal ? '#3b82f6' : '#cbd5e1'}`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <CloudQueueIcon sx={{ fontSize: 18, color: mostrarMicrosoftGlobal ? '#0078d4' : '#64748b' }} />
                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: mostrarMicrosoftGlobal ? '#1e40af' : '#475569' }}>
                    Login Microsoft:
                  </Typography>
                  <Switch
                    size="small"
                    checked={mostrarMicrosoftGlobal}
                    onChange={(e) => handleToggleMicrosoftGlobal(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#0078d4' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0078d4' },
                    }}
                  />
                  <Chip
                    label={mostrarMicrosoftGlobal ? 'VISIBLE' : 'OCULTO'}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 800,
                      bgcolor: mostrarMicrosoftGlobal ? '#0078d4' : '#94a3b8',
                      color: '#ffffff',
                    }}
                  />
                </Box>
              </Tooltip>
            </Stack>
          }
          rightButtons={
            <ExportButton
              data={dataForTable}
              fileName="Usuarios.xlsx"
              columnMap={[
                { key: 'USUARIOID', label: 'ID' },
                { key: 'USER_NAME', label: 'USUARIO' },
                { key: 'CORREO', label: 'CORREO' },
                { key: 'TIPO_USUARIO', label: 'TIPO DE USUARIO' },
                { key: 'EMPRESA', label: 'EMPRESA(S)' },
                { key: 'ACTIVO', label: 'ACTIVO' },
              ]}
            />
          }
          mobileCard={{ statusMode: 'none' }}
          initialColumnOrder={[
            'USUARIO_USUARIO_ID',
            'ESTATUS_CONEXION',
            'SEGURIDAD_2FA',
            'TIEMPO_ACTIVIDAD',
            'ULTIMA_IP',
            'DISPOSITIVO',
            'CORREO',
            'TIPO_USUARIO',
            'EMPRESAS_ASIGNADAS',
            'ESTATUS_COMPACTO',
          ]}
          hiddenFields={['PASSWORD', 'TIPOUSERID', 'EMPRESAID', 'FECHACREACION', 'ACCESOS_JSON', 'NOMBRE_EMPRESA', 'TIPO_USER', 'USUARIOID', 'USER_NAME', 'USERNAME', 'EMPRESA', 'ACTIVO', 'ultimo_acceso', 'ultima_actividad', 'bloqueado_hasta', 'intentos_fallidos', 'requiere_2fa', 'esta_bloqueado']}
          columnLabels={{
            USUARIO_USUARIO_ID: 'Usuario ERP / ID',
            ESTATUS_CONEXION: 'Conexión',
            SEGURIDAD_2FA: 'Seguridad 2FA',
            TIEMPO_ACTIVIDAD: 'Última Actividad',
            ULTIMA_IP: 'Última IP',
            DISPOSITIVO: 'Navegador / SO',
            CORREO: 'Correo Electrónico',
            TIPO_USUARIO: 'Tipo de Usuario',
            EMPRESAS_ASIGNADAS: 'Empresas Asignadas',
            ESTATUS_COMPACTO: 'Estatus',
          }}
          density="small"
          compactActions
          maxInlineActions={4}
          emptyStateData={{
            title: 'No se encontraron usuarios',
            description: 'No hay usuarios disponibles para mostrar.',
          }}
          loading={loadingData}
          serverPagination={false}
          usuarioId={usuarioId}
          empresaId={empresaIdPref}
          apiUrl={apiUrl}
          persistEnabled
          persistKey="Usuarios_v5"
          persistVista="layout"
        />
      </Box>

      <ModalUsuarios
        isOpen={modalOpen}
        handleClose={handleCloseModal}
        handleSave={createUpdate}
        formData={formData}
        handleInputChange={handleInputChange}
        dataEmpresa={dataEmpresa}
        dataRool={dataRool}
        editPasswordOnly={editPasswordOnly}
      />

      <ModalPermisos
        open={permissionModalOpen}
        onClose={cerrarPermisosUsuario}
        onSave={handlePermisosGuardados}
        userId={selectedUser?.USUARIOID}
        userName={selectedUser?.USER_NAME}
      />

      <DoubleConfirmModal
        open={confirmModal.open}
        message={confirmModal.message}
        handleConfirm={handleConfirmAction}
        handleCancel={handleCancelAction}
      />
    </Box>
  );
}